import { config } from '../../../config';
import { getAnonToken } from '../../lib/anonUtils';
import { getCustomLogger } from '../../lib/logger';
import { GetAnonymousDataResponseBody } from '../../services/anonService';
import { processImportFileAnonUser } from '../../services/fileOpsService';
import { sanitizeFileName } from './common';

const logger = getCustomLogger('Microtools: common v2');

export const finishProcessingWithRedirectV2 = async (
  url: string,
  anonymousData: GetAnonymousDataResponseBody | null,
  openInNewTab?: boolean,
) => {
  // Get the part after the hash
  const hashPart = window.location.hash.split('?')[1] || '';
  const urlParams = new URLSearchParams(hashPart);
  const intent = urlParams.get('intent') || '';

  let redirectUrl = '';
  // Anonymous data is used to start the import process
  if (anonymousData) {
    const folder = 'recents';
    const response = await processImportFileAnonUser(
      anonymousData.team.selectedTeam.teamId,
      folder,
      url,
      {
        intent,
      },
    );
    if (response.data.success) {
      // Attach the anon token to the url in this context of anonymousData
      const anonToken = getAnonToken() ?? '';
      redirectUrl = `${
        config.appBaseUrl
      }/#/dashboard?anonToken=${encodeURIComponent(anonToken)}${
        intent ? `&intent=${encodeURIComponent(intent)}` : ''
      }`;
    }
  }
  // If anonymous data is not used to start the import process, use the regular import after redirect
  if (!redirectUrl) {
    redirectUrl = `${
      config.appBaseUrl
    }/#/dashboard?importUrl=${encodeURIComponent(url)}&importFlow=v2${
      intent ? `&intent=${encodeURIComponent(intent)}` : ''
    }`;
  }

  if (openInNewTab) {
    window.open(redirectUrl, '_blank');
  } else {
    window.parent.location.href = redirectUrl;
  }
};

const uploadFileToS3Async = (
  blob: Blob,
  uploadUrl: string,
  downloadUrl: string,
  anonymousData: GetAnonymousDataResponseBody | null,
  onProgress: (progress: number) => void,
  onComplete: (downloadUrl0: string) => void,
  skipRedirect?: boolean,
  openInNewTab?: boolean,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', blob.type);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    };

    xhr.onload = async () => {
      if (xhr.status === 200) {
        try {
          onComplete(downloadUrl);
          if (!skipRedirect) {
            await finishProcessingWithRedirectV2(
              downloadUrl,
              anonymousData,
              openInNewTab,
            );
          }
          resolve();
        } catch (err) {
          reject(err);
        }
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));

    xhr.send(blob);
  });
};

const uploadToScreenAppFileOpsV2 = async (
  blob: Blob,
  fileName: string,
  anonymousData: GetAnonymousDataResponseBody | null,
  onStart: () => void,
  onProgress: (progress: number) => void,
  onComplete: (downloadUrl: string) => void,
  onError: (error: string) => void,
  skipRedirect?: boolean,
  openInNewTab?: boolean,
) => {
  onStart();

  try {
    const url = `${config.authBaseUrlV2}/file-ops/phase/connect`;
    const sanitizedFileName = sanitizeFileName(fileName);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: sanitizedFileName }),
    });

    const data = await response.json();
    if (data.success) {
      await uploadFileToS3Async(
        blob,
        data.data.uploadUrl,
        data.data.downloadUrl,
        anonymousData,
        onProgress,
        onComplete,
        skipRedirect,
        openInNewTab,
      );
    } else {
      throw new Error(data.message || 'Failed to get download URL');
    }
  } catch (error) {
    logger.error('Error uploading to screen app', error);
    const extensionMessageList = [
      'Please upload an audio or video media file.',
    ];
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (extensionMessageList.some((msg) => errorMessage.includes(msg))) {
      onError(
        'Unsupported file type. Please upload a valid audio or video file.',
      );
    } else {
      onError('Upload failed. Please try again.');
    }
  }
};

export default uploadToScreenAppFileOpsV2;
